// ─── Discharge ↔ impairment cross-reference (Q3) ────────────────────────────────
//
// Two-step deterministic join, no string similarity, no LLM (see KNOWN_ISSUES.md):
//   1. facility DMR parameter code → ECHO pollutant category  (EPA crosswalk, fetched)
//   2. ECHO pollutant category     → ATTAINS impairment group  (CURATED map below)
// then intersect the resulting groups with the watershed's actual impairment causes.
//
// EPA does NOT publish step 2, so this table is hand-curated and conservative.
// It is a compliance-sensitive artifact: only assert a link where the category is a
// defensible equivalent of the ATTAINS group. `high` pairs ship by default; `borderline`
// pairs are gated behind `includeBorderline` pending domain-expert sign-off. Broad
// chemical buckets (CWA_PRIORITY_POLLUTANTS, CERCLA_HAZ_SUBST, TRI_CHEMICALS) and
// non-pollutants (WASTEWATER_FLOW) are intentionally left unmapped — too broad to
// assert a specific impairment link.

export interface ICategoryGroupMapping {
  category: string;
  attainsGroups: string[];
  confidence: 'high' | 'borderline';
}

export const ECHO_CATEGORY_TO_ATTAINS: ICategoryGroupMapping[] = [
  // High confidence — direct, defensible equivalents.
  { category: 'ORGANIC_ENRICHMENT', attainsGroups: ['ORGANIC ENRICHMENT/OXYGEN DEPLETION'], confidence: 'high' },
  { category: 'NITROGEN', attainsGroups: ['NUTRIENTS'], confidence: 'high' },
  { category: 'PHOSPHORUS', attainsGroups: ['NUTRIENTS'], confidence: 'high' },
  { category: 'PATHOGEN_INDICATORS', attainsGroups: ['PATHOGENS'], confidence: 'high' },
  { category: 'METALS', attainsGroups: ['METALS (OTHER THAN MERCURY)'], confidence: 'high' },
  { category: 'TEMPERATURE', attainsGroups: ['TEMPERATURE'], confidence: 'high' },
  { category: 'PFAS', attainsGroups: ['PER- AND POLYFLUOROALKYL SUBSTANCES (PFAS)'], confidence: 'high' },

  // Borderline — plausible but need domain sign-off before shipping.
  { category: 'SOLIDS', attainsGroups: ['SEDIMENT', 'TURBIDITY'], confidence: 'borderline' },
  { category: 'RADIONUCLIDES', attainsGroups: ['RADIATION'], confidence: 'borderline' },
  { category: 'COLOR', attainsGroups: ['TASTE, COLOR, AND ODOR'], confidence: 'borderline' },
];

export interface IFacilityParam {
  code: string;
  name: string;
}

export interface IImpairmentLink {
  parameterName: string;
  parameterCode: string;
  category: string;
  attainsGroup: string;
}

export interface IDischargeImpairmentResult {
  links: IImpairmentLink[];
  facilityParamNames: string[];
  watershedCauses: string[];
}

/**
 * Cross-reference a facility's DMR parameters against a watershed's ATTAINS
 * impairment causes, using the fetched DMR→category crosswalk and the curated
 * category→group map. Returns the flagged links plus both raw lists so the UI
 * can render an honest non-link when `links` is empty.
 */
export function matchDischargeToImpairments(
  params: IFacilityParam[],
  crosswalk: Record<string, string[]>,
  impairmentGroups: string[],
  opts: { includeBorderline?: boolean } = {}
): IDischargeImpairmentResult {
  const impairedSet = new Set(impairmentGroups.map((g) => g.toUpperCase().trim()));

  // category -> attainsGroups, respecting the borderline gate
  const categoryMap = new Map<string, string[]>();
  ECHO_CATEGORY_TO_ATTAINS.forEach((m) => {
    if (m.confidence === 'borderline' && !opts.includeBorderline) return;
    categoryMap.set(m.category, m.attainsGroups);
  });

  const seen = new Set<string>();
  const links: IImpairmentLink[] = [];
  const distinctNames = new Map<string, string>(); // code -> name (dedup)

  params.forEach((p) => {
    if (!p.code) return;
    if (!distinctNames.has(p.code)) distinctNames.set(p.code, p.name);
    const cats = crosswalk[p.code];
    if (!cats) return;
    cats.forEach((cat) => {
      const groups = categoryMap.get(cat);
      if (!groups) return;
      groups.forEach((group) => {
        if (!impairedSet.has(group.toUpperCase().trim())) return;
        const key = `${p.code}|${group}`;
        if (seen.has(key)) return;
        seen.add(key);
        links.push({
          parameterName: p.name,
          parameterCode: p.code,
          category: cat,
          attainsGroup: group,
        });
      });
    });
  });

  return {
    links,
    facilityParamNames: Array.from(distinctNames.values()),
    watershedCauses: impairmentGroups,
  };
}
